import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Info, CheckCircle2, Lock, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Loading, Crumbs, ShareMenu } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

export default function ConsultationDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [voting, setVoting] = useState(false);
  const { user, login } = useAuth();

  const load = useCallback(() => api.get(`/consultations/${id}`).then((r) => setC(r.data)).catch(() => setC(false)), [id]);
  useEffect(() => { load(); }, [load]);

  if (c === null) return <Loading />;
  if (c === false) return <div className="max-w-4xl mx-auto px-6 py-20 text-center text-slate-400">Consulta no encontrada.</div>;

  const total = c.total_votes || 0;
  const results = c.results || {};

  const castVote = async (optionId) => {
    if (!user) { toast.info("Crea tu cuenta verificada para votar."); login(); return; }
    setVoting(true);
    try {
      await api.post(`/consultations/${id}/vote`, { option_id: optionId });
      toast.success("Tu 2Voto quedó registrado.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar tu voto.");
    } finally { setVoting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="consultation-detail">
      <Crumbs items={[{ label: "Emite tu 2Voto", to: "/consultas" }, { label: c.title }]} />
      <div className="flex justify-end -mt-2 mb-2"><ShareMenu title={c.title} /></div>
      {c.category && <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">{c.category}</span>}
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight mt-2 mb-5">{c.title}</h1>

      <div className="bg-slate-100 rounded-lg p-5 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
        <p className="text-slate-700 text-sm leading-relaxed">{c.context}</p>
      </div>

      {/* Arguments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="flex items-center gap-2 font-heading font-bold text-slate-900 mb-3"><ThumbsUp className="w-4 h-4 text-cyan-500" /> Argumentos a favor</h3>
          <ul className="space-y-3">
            {(c.arguments_for || []).map((a) => (
              <li key={a.text} className="text-sm text-slate-600">
                {a.text}
                {a.source && <span className="block text-xs text-slate-400 mt-0.5">Fuente: {a.source}</span>}
              </li>
            ))}
            {(!c.arguments_for || c.arguments_for.length === 0) && <li className="text-sm text-slate-400">Sin argumentos registrados.</li>}
          </ul>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="flex items-center gap-2 font-heading font-bold text-slate-900 mb-3"><ThumbsDown className="w-4 h-4 text-rose-500" /> Argumentos en contra</h3>
          <ul className="space-y-3">
            {(c.arguments_against || []).map((a) => (
              <li key={a.text} className="text-sm text-slate-600">
                {a.text}
                {a.source && <span className="block text-xs text-slate-400 mt-0.5">Fuente: {a.source}</span>}
              </li>
            ))}
            {(!c.arguments_against || c.arguments_against.length === 0) && <li className="text-sm text-slate-400">Sin argumentos registrados.</li>}
          </ul>
        </div>
      </div>

      {/* Voting */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <h3 className="font-heading font-bold text-xl text-slate-900 mb-4">Emite tu 2Voto</h3>
        <div className="space-y-3">
          {c.options.map((o) => {
            const count = results[o.id] || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const selected = c.user_vote === o.id;
            return (
              <button key={o.id} onClick={() => castVote(o.id)} disabled={voting}
                data-testid={`vote-option-${o.id}`}
                className={`w-full text-left rounded-lg border p-4 transition-all relative overflow-hidden ${selected ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-cyan-300 hover:bg-slate-50"}`}>
                {c.show_results && <div className="absolute inset-0 bg-cyan-100/50" style={{ width: `${pct}%` }} />}
                <div className="relative flex items-center justify-between">
                  <span className="font-medium text-slate-800 flex items-center gap-2">
                    {selected && <CheckCircle2 className="w-4 h-4 text-cyan-600" />}
                    {o.label}
                  </span>
                  {c.show_results && <span className="text-sm font-semibold text-slate-600">{pct}% · {count}</span>}
                </div>
              </button>
            );
          })}
        </div>
        {!c.show_results && (
          <p className="flex items-center gap-2 text-sm text-slate-500 mt-4"><Lock className="w-4 h-4" /> Los resultados se muestran después de votar para reducir sesgo.</p>
        )}
        {c.user_vote && <p className="text-sm text-cyan-600 mt-4">Ya participaste. Puedes cambiar tu voto mientras la consulta esté abierta.</p>}
      </div>

      {/* Methodology + sources */}
      <div className="bg-slate-100 rounded-lg p-5 text-sm text-slate-600">
        <p className="font-semibold text-slate-800 mb-1">Metodología</p>
        <p className="mb-3">{c.methodology}</p>
        <p><strong>Participaciones:</strong> {total}</p>
        {c.period_end && <p><strong>Cierre:</strong> {new Date(c.period_end).toLocaleDateString("es-PR")}</p>}
        {c.sources?.length > 0 && (
          <div className="mt-3">
            <p className="font-semibold text-slate-800 mb-1">Fuentes</p>
            {c.sources.map((s) => (
              <a key={s} href={s} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cyan-600 hover:underline break-all">
                <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {s}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
